import { useState, useMemo, FormEvent, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { SelectOption } from "@idw-types/common";
import {
    FormattedIdentityProviderConfiguration
} from "@idw-types/data/identity-providers";
import { IDENTITY_PROVIDER_CREATE_METHOD_OPTIONS } from "@constants/identity-provider-create-method-options";
import { IDENTITY_PROVIDERS_DETAILS_URL } from "@constants/routes";
import { IdentityProviderCreateMethodTypes } from "@enums/identity-provider-create-method-types";
import { injectIdentityProviderId } from "@utils/parameter-injector";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { IdentityProviderSelectFormGroup } from "../../common/select-form-groups";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import defaultStyles from "../../common/modals/default-modal/default-modal.less";
import { navigateToUtil } from "@utils/navigation-util";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import {
    createIdentityProviderRequest,
    CreateIdentityProviderRequestResponse
} from "@requests/identity-provider";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";

type AddIdentityProviderModalProps = {
    onModalClose: () => void;
    identityProviderConfigurations: Array<FormattedIdentityProviderConfiguration>;
};

type AddIdentityProviderModalState = {
    requestInProgress: boolean;
    selectedCreateMethod: SelectOption;
    selectedFileName: string;
    selectedFile: File;
    selectedIdentityProviderOption: SelectOption;
};

type AddIdentityProviderModalValidationState = {
    selectedFileJSON: any;
};

export const AddIdentityProviderModal = (props: AddIdentityProviderModalProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const [labelWidth] = useState(LabelWidthTypes.SMALL);

    const { onModalClose, identityProviderConfigurations } = props;
    const [state, setState] = useState<AddIdentityProviderModalState>({
        requestInProgress: false,
        selectedCreateMethod: IDENTITY_PROVIDER_CREATE_METHOD_OPTIONS[0],
        selectedFileName: "",
        selectedFile: null,
        selectedIdentityProviderOption: null
    });
    const [validationState, setValidationState] = useState<AddIdentityProviderModalValidationState>({
        selectedFileJSON: null
    });

    const handleCreateResponse = (data: CreateIdentityProviderRequestResponse, error: any) => {
        if (data?.response) {
            displaySuccessModal(
                dispatch,
                resources.requests.createIdentityProvider.success.title,
                resources.requests.createIdentityProvider.success.message
            );
            onModalClose();
            const url = injectIdentityProviderId(IDENTITY_PROVIDERS_DETAILS_URL, data.response.identityProviderID);
            navigateToUtil(url, navigate, routerLocation.pathname);
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createIdentityProvider.error.title,
                resources.requests.createIdentityProvider.error.message
            );
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUseBlankSubmit = async () => {
        const { data, error } = await createIdentityProviderRequest(undefined, undefined);
        handleCreateResponse(data, error);
    };

    const handleUseImportSubmit = async () => {
        const { data, error } = await createIdentityProviderRequest(null, validationState.selectedFileJSON);
        handleCreateResponse(data, error);
    };

    const handleUseCopySubmit = async () => {
        const { data, error } = await createIdentityProviderRequest(state.selectedIdentityProviderOption.value, undefined);
        handleCreateResponse(data, error);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({
            ...state,
            requestInProgress: true
        });

        switch (state.selectedCreateMethod.value) {
            case IdentityProviderCreateMethodTypes.USE_BLANK:
                return handleUseBlankSubmit();
            case IdentityProviderCreateMethodTypes.USE_IMPORT:
                return handleUseImportSubmit();
            case IdentityProviderCreateMethodTypes.USE_COPY:
                return handleUseCopySubmit();
        }
    };

    const handleUpdateIWantTo = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedCreateMethod: newValue
            });
        }
    };

    const handleUpdateFile = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files.length > 0) {
            setState({ ...state, selectedFileName: event.target.value || "", selectedFile: event.target.files[0] });

            try {
                const fileReader = new FileReader();
                fileReader.onload = function () {
                    const jsonVerificationService = JSON.parse(fileReader.result as string);
                    setValidationState({ selectedFileJSON: jsonVerificationService });
                };
                fileReader.readAsText(event.target.files[0]);
            } catch (error) {
                setState({ ...state, selectedFileName: "", selectedFile: null });
                setValidationState({ selectedFileJSON: null });
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.modals.importVerificationServiceConfiguration.loadFile.error.title,
                    resources.modals.importVerificationServiceConfiguration.loadFile.error.message
                );
            }
        } else {
            setState({ ...state, selectedFileName: "", selectedFile: null });
            setValidationState({ selectedFileJSON: null });
        }
    };

    const handleUpdateIdentityProvider = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedIdentityProviderOption: newValue
            });
        }
    };

    const buttonLabel = useMemo(() => {
        switch (state.selectedCreateMethod.value) {
            case IdentityProviderCreateMethodTypes.USE_BLANK:
                return resources.modals.addIdentityProvider.buttons.useBlank.label;
            case IdentityProviderCreateMethodTypes.USE_COPY:
                return resources.modals.addIdentityProvider.buttons.useCopy.label;
            case IdentityProviderCreateMethodTypes.USE_IMPORT:
                return resources.modals.addIdentityProvider.buttons.useImport.label;
            default:
                return resources.common.buttons.save.label;
        }
    }, [state.selectedCreateMethod]);

    return (
        <DefaultModal
            automationId="add-identity-provider-modal"
            ariaLabelledBy="add-identity-provider-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-identity-provider-title"} closeButton>
                    <Modal.Title>{resources.modals.addIdentityProvider.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {resources.modals.addIdentityProvider.description}
                    </span>
                    <SelectFormGroup
                        controlId={`identity-provider-iwantto-select`}
                        label={resources.modals.addIdentityProvider.inputs.iWantTo.label}
                        ariaLabel={resources.modals.addIdentityProvider.inputs.iWantTo.label}
                        automationId={`identity-provider-iwantto-select`}
                        onChange={handleUpdateIWantTo}
                        options={IDENTITY_PROVIDER_CREATE_METHOD_OPTIONS}
                        selectedOption={state.selectedCreateMethod}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                    {state.selectedCreateMethod.value === IdentityProviderCreateMethodTypes.USE_IMPORT && (
                        <InputFormGroup
                            controlId="identity-provider-file-input"
                            label={resources.modals.addIdentityProvider.inputs.file.label}
                            ariaLabel={resources.modals.addIdentityProvider.inputs.file.label}
                            automationId="identity-provider-file-input"
                            value={state.selectedFileName}
                            type={"file"}
                            accept={"application/json"}
                            onChange={handleUpdateFile}
                            disabled={state.requestInProgress}
                            isRequired={true}
                            labelWidth={labelWidth}
                        />
                    )}
                    {state.selectedCreateMethod.value === IdentityProviderCreateMethodTypes.USE_COPY && (
                        <IdentityProviderSelectFormGroup
                            idPrefix={"identity-provider"}
                            onChange={handleUpdateIdentityProvider}
                            identityProviders={identityProviderConfigurations}
                            selectedOption={state.selectedIdentityProviderOption}
                            disabled={state.requestInProgress}
                            isRequired={true}
                            isSearchable={true}
                            isClearable={false}
                            labelWidth={labelWidth}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-identity-provider-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-identity-provider-submit-button"
                        disabled={state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={buttonLabel}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};